import { useEffect, useState } from "react";
import moment from "moment";
import { fetchAPIquery, getSubdomain } from "../../utils/utils";
import { downloadImage } from "../../utils/upload-image";
import { PLACEHOLDER_PROFILE_NO_PHOTO } from "../../utils/placeholders";
import { getCastellerFullName } from "../../utils/castellerName";

const COLLA = getSubdomain();

// Modal for enlarged profile picture
const ProfilePicModal = ({ imgSrc, onClose, name }) => {
    useEffect(() => {
        const handleEsc = (e) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', handleEsc);
        return () => document.removeEventListener('keydown', handleEsc);
    }, [onClose]);

    const handleOverlayClick = (e) => {
        e.stopPropagation();
        onClose();
    };

    return (
        <div 
            className="stats-positions-profile-modal-overlay"
            onClick={handleOverlayClick}
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 9999,
                cursor: 'pointer',
            }}
        >
            <div 
                onClick={(e) => e.stopPropagation()}
                style={{
                    position: 'relative',
                    maxWidth: '90vw',
                    maxHeight: '90vh',
                }}
            >
                <img 
                    src={imgSrc}
                    alt={name || ''}
                    style={{
                        maxWidth: '90vw',
                        maxHeight: '85vh',
                        borderRadius: 12,
                        objectFit: 'contain',
                        boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
                    }}
                />
                {name ? (
                    <div style={{
                        textAlign: 'center',
                        color: 'white',
                        marginTop: 12,
                        fontSize: '1.1rem',
                        fontWeight: 500,
                    }}>
                        {name}
                    </div>
                ) : null}
                <button
                    onClick={onClose}
                    style={{
                        position: 'absolute',
                        top: -12,
                        right: -12,
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        border: 'none',
                        backgroundColor: 'white',
                        color: '#333',
                        fontSize: 18,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                    }}
                >
                    ×
                </button>
            </div>
        </div>
    );
};

// Small profile picture component that loads lazily
const ProfilePicSmall = ({ castellerId, name }) => {
    const [profilePic, setProfilePic] = useState(null);
    const [showModal, setShowModal] = useState(false);

    useEffect(() => {
        let mounted = true;
        downloadImage(castellerId, COLLA)
            .then(base64 => {
                if (mounted) setProfilePic(base64 || false);
            })
            .catch(() => {
                if (mounted) setProfilePic(false);
            });
        return () => { mounted = false; };
    }, [castellerId]);

    const isLoading = profilePic === null;
    const imgSrc = profilePic || PLACEHOLDER_PROFILE_NO_PHOTO;

    const handleClick = (e) => {
        e.stopPropagation();
        if (profilePic) {
            setShowModal(true);
        }
    };

    return (
        <>
            {isLoading ? (
                <div 
                    className="stats-positions-ranking-avatar-loading"
                    style={{
                        width: 36,
                        height: 36,
                        borderRadius: 6,
                        marginRight: 10,
                        flexShrink: 0,
                        backgroundColor: '#e0e0e0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <div 
                        className="stats-positions-avatar-spinner"
                        style={{
                            width: 16,
                            height: 16,
                            border: '2px solid #ccc',
                            borderTopColor: '#888',
                            borderRadius: '50%',
                            animation: 'spin 0.8s linear infinite',
                        }}
                    />
                </div>
            ) : (
                <img 
                    src={imgSrc}
                    alt=""
                    className="stats-positions-ranking-avatar"
                    onClick={handleClick}
                    style={{
                        width: 36,
                        height: 36,
                        borderRadius: 6,
                        objectFit: 'cover',
                        marginRight: 10,
                        flexShrink: 0,
                        backgroundColor: '#e0e0e0',
                        cursor: profilePic ? 'pointer' : 'default',
                    }}
                />
            )}
            {showModal ? (
                <ProfilePicModal 
                    imgSrc={imgSrc} 
                    onClose={() => setShowModal(false)} 
                    name={name}
                />
            ) : null}
        </>
    );
};

const TAB_DEFINITIONS = [
    { key: 'pinya', label: 'Pinya', color: '#3b82f6' },
    { key: 'folre', label: 'Folre', color: '#f59e0b' },
    { key: 'manilles', label: 'Manilles', color: '#10b981' },
    { key: 'puntals', label: 'Puntals', color: '#ef4444' },
    { key: 'tronc', label: 'Tronc', color: '#8b5cf6' },
    { key: 'organitzacio', label: 'Organització', color: '#ec4899' },
];

const CORDO_DEFINITIONS = [
    { key: 'nucli', label: 'N', color: '#a78bfa' },
    { key: '1r', label: '1', color: '#60a5fa' },
    { key: '2n', label: '2', color: '#34d399' },
    { key: '3r', label: '3', color: '#fbbf24' },
    { key: '+4', label: '+4', color: '#f87171' }
];

const PESTANYA_INITIALS = {
    pinya: 'P',
    folre: 'F',
    manilles: 'M',
    puntals: 'Pu',
    tronc: 'T',
    organitzacio: 'O'
};

// Combined mini charts with connecting lines from P to cordos
const MiniChartsWithConnectors = ({ pestanyes, cordos, unpaintedProvesCount }) => {
    const pestanyesValues = TAB_DEFINITIONS.map(def => pestanyes?.[def.key] || 0);
    const unpaintedValue = unpaintedProvesCount || 0;
    const maxPestanyaValue = Math.max(...pestanyesValues, unpaintedValue, 1);
    const hasPestanyes = pestanyesValues.some(v => v > 0) || unpaintedValue > 0;
    
    const pinyaTotal = pestanyes?.pinya || 0;
    
    if (!hasPestanyes) return <span className="stats-positions-mini-chart-empty">-</span>;
    
    // Find which pestanyes are visible (non-zero), excluding organitzacio
    const visiblePestanyes = TAB_DEFINITIONS.map((def, i) => ({
        ...def,
        value: pestanyes?.[def.key] || 0,
        originalIndex: i
    })).filter(p => p.value > 0 && p.key !== 'organitzacio');
    
    // All cordos (show all even if 0 for consistent row height)
    const allCordos = CORDO_DEFINITIONS.map((def, i) => ({
        ...def,
        value: cordos?.[def.key] || 0,
        originalIndex: i
    }));
    
    return (
        <div className="stats-positions-mini-charts-combined">
            {/* Pestanyes chart */}
            <div className="stats-positions-mini-chart-column">
                <span className="stats-positions-mini-chart-title">Pisos</span>
                <div className="stats-positions-mini-chart-vertical" title={`Pinya: ${pestanyes.pinya}, Folre: ${pestanyes.folre}, Manilles: ${pestanyes.manilles}, Puntals: ${pestanyes.puntals}, Tronc: ${pestanyes.tronc}, Org: ${pestanyes.organitzacio}, Sense pintar: ${unpaintedValue}`}>
                    {unpaintedValue > 0 ? (
                        <div key="unpainted" className="stats-positions-mini-chart-row">
                            <span className="stats-positions-mini-chart-label" title="Proves sense pintar">∅</span>
                            <div className="stats-positions-mini-chart-bar-container">
                                <div 
                                    className="stats-positions-mini-chart-bar-fill"
                                    style={{ width: `${(unpaintedValue / maxPestanyaValue) * 100}%`, backgroundColor: '#9ca3af' }}
                                />
                            </div>
                        </div>
                    ) : null}
                    {visiblePestanyes.map(def => {
                        const width = (def.value / maxPestanyaValue) * 100;
                        return (
                            <div key={def.key} className="stats-positions-mini-chart-row">
                                <span className="stats-positions-mini-chart-label">{PESTANYA_INITIALS[def.key]}</span>
                                <div className="stats-positions-mini-chart-bar-container">
                                    <div 
                                        className="stats-positions-mini-chart-bar-fill"
                                        style={{ width: `${width}%`, backgroundColor: def.color }}
                                    />
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
            
            {/* Spacer between charts */}
            <div style={{ width: 8 }} />
            
            {/* Cordos chart - uses same scale as pestanyes so visual sum equals P bar */}
            {pinyaTotal > 0 ? (
                <div className="stats-positions-mini-chart-column">
                    <span className="stats-positions-mini-chart-title">Pinya</span>
                    <div className="stats-positions-mini-chart-vertical" title={`Nucli: ${cordos.nucli}, 1r: ${cordos['1r']}, 2n: ${cordos['2n']}, 3r: ${cordos['3r']}, +4: ${cordos['+4']}`}>
                        {allCordos.map(def => {
                            const width = (def.value / maxPestanyaValue) * 100;
                            return (
                                <div key={def.key} className="stats-positions-mini-chart-row">
                                    <span className="stats-positions-mini-chart-label">{def.label}</span>
                                    <div className="stats-positions-mini-chart-bar-container">
                                        <div 
                                            className="stats-positions-mini-chart-bar-fill"
                                            style={{ width: `${width}%`, backgroundColor: def.color }}
                                        />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            ) : <span className="stats-positions-mini-chart-empty">-</span>}
        </div>
    );
};

function StatsComparativaPosicions({ 
    castellersInfo, 
    start_date, 
    end_date, 
    includeAssaigs, 
    includeActuacions, 
    onSelectUser 
}) {
    const [allCastellersScores, setAllCastellersScores] = useState([]);
    const [totalEvents, setTotalEvents] = useState(0);
    const [loadingScores, setLoadingScores] = useState(false);
    const [rankingLimitBest, setRankingLimitBest] = useState(10);
    const [rankingLimitWorst, setRankingLimitWorst] = useState(10);

    // Fetch all castellers scores (cached)
    useEffect(() => {
        if (!start_date || !end_date) {
            setAllCastellersScores([]);
            setTotalEvents(0);
            return;
        }

        setLoadingScores(true);

        fetchAPIquery('/stats/comparativa-posicions', 
            (response) => {
                if (response?.scores) {
                    setAllCastellersScores(response.scores);
                    setTotalEvents(response.totalEvents || 0);
                } else {
                    setAllCastellersScores([]);
                    setTotalEvents(0);
                }
                setLoadingScores(false);
            },
            true, // withCache
            true, // saveCache
            {
                start: moment(start_date).format('YYYY-MM-DD'),
                end: moment(end_date).format('YYYY-MM-DD'),
                assaigs: includeAssaigs ? '1' : '0',
                actuacions: includeActuacions ? '1' : '0'
            }
        );
    }, [start_date, end_date, includeAssaigs, includeActuacions]);

    // Filter out castellers with:
    // - less than 10 positions
    // - canalla
    // - less than 10% attendance
    const MIN_POSITIONS = 10;
    const MIN_ATTENDANCE_PERCENT = 10;
    const minEventsRequired = Math.max(1, Math.floor(totalEvents * MIN_ATTENDANCE_PERCENT / 100));
    const filteredScores = allCastellersScores
        .filter(item => item.positionsCount >= MIN_POSITIONS)
        .filter(item => castellersInfo?.[item.castellerId]?.canalla !== 1)
        .filter(item => item.eventsAttended >= minEventsRequired)

    return (
        <div className="stats-positions-section">
            <h2>Comparativa de posicions</h2>
            <p className="stats-positions-description">
                Anàlisi de la distribució de posicions per casteller. Mostra en quins pisos (tronc, manilles, folre...) 
                i en quins cordons de la pinya (nucli, 1r, 2n...) ha participat cada persona durant el període seleccionat.
            </p>
            {loadingScores ? (
                <div className="stats-positions-etiquetes-loading">
                    <span className="stats-positions-spinner"></span>
                    Carregant dades...
                </div>
            ) : filteredScores.length === 0 ? (
                <p>No hi ha dades de posicions per aquest període (mínim {MIN_POSITIONS} proves i {MIN_ATTENDANCE_PERCENT}% d'assistència).</p>
            ) : (
                <div className="stats-positions-ranking-row">
                    <div className="stats-positions-ranking-column">
                        <h3 className="stats-positions-ranking-title stats-positions-ranking-title-best">
                            Castellers amb més posicions centrals
                        </h3>
                        <ol className="stats-positions-ranking-list">
                            {filteredScores.slice(0, rankingLimitBest).map((item, index) => {
                                const casteller = castellersInfo?.[item.castellerId];
                                const mote = casteller?.mote;
                                const fullName = casteller ? getCastellerFullName(casteller) : `#${item.castellerId}`;
                                const isLesionat = casteller?.lesionat;
                                const isJunta = casteller?.es_junta;
                                const isTecnica = casteller?.es_tecnica;
                                const isCanalla = casteller?.canalla;
                                return (
                                    <li 
                                        key={item.castellerId} 
                                        className="stats-positions-ranking-item"
                                        onClick={() => onSelectUser(item.castellerId)}
                                    >
                                        <span className="stats-positions-ranking-position">#{index + 1}</span>
                                        <ProfilePicSmall castellerId={item.castellerId} name={mote || fullName} />
                                        <div className="stats-positions-ranking-name-container">
                                            <span className="stats-positions-ranking-name">
                                                {isCanalla ? '👶 ' : null}
                                                {mote || fullName}
                                                {isJunta ? <span className="stats-positions-ranking-badge stats-positions-ranking-badge-junta">J</span> : null}
                                                {isTecnica ? <span className="stats-positions-ranking-badge stats-positions-ranking-badge-tecnica">T</span> : null}
                                                {isLesionat ? ' 🏥' : ''}
                                            </span>
                                            {mote ? <span className="stats-positions-ranking-fullname">{fullName}</span> : null}
                                        </div>
                                        <MiniChartsWithConnectors pestanyes={item.pestanyes} cordos={item.cordos} unpaintedProvesCount={item.unpaintedProvesCount} />
                                    </li>
                                );
                            })}
                        </ol>
                        {rankingLimitBest < filteredScores.length ? (
                            <button 
                                className="stats-positions-ranking-load-more"
                                onClick={() => setRankingLimitBest(prev => prev + 10)}
                            >
                                Carregar 10 més
                            </button>
                        ) : null}
                    </div>
                    <div className="stats-positions-ranking-column">
                        <h3 className="stats-positions-ranking-title stats-positions-ranking-title-worst">
                            Castellers amb menys posicions centrals
                        </h3>
                        <ol className="stats-positions-ranking-list">
                            {[...filteredScores].reverse().slice(0, rankingLimitWorst).map((item, index) => {
                                const casteller = castellersInfo?.[item.castellerId];
                                const mote = casteller?.mote;
                                const fullName = casteller ? getCastellerFullName(casteller) : `#${item.castellerId}`;
                                const isLesionat = casteller?.lesionat;
                                const isJunta = casteller?.es_junta;
                                const isTecnica = casteller?.es_tecnica;
                                const isCanalla = casteller?.canalla;
                                return (
                                    <li 
                                        key={item.castellerId} 
                                        className="stats-positions-ranking-item"
                                        onClick={() => onSelectUser(item.castellerId)}
                                    >
                                        <span className="stats-positions-ranking-position">#{index + 1}</span>
                                        <ProfilePicSmall castellerId={item.castellerId} name={mote || fullName} />
                                        <div className="stats-positions-ranking-name-container">
                                            <span className="stats-positions-ranking-name">
                                                {isCanalla ? '👶 ' : null}
                                                {mote || fullName}
                                                {isJunta ? <span className="stats-positions-ranking-badge stats-positions-ranking-badge-junta">J</span> : null}
                                                {isTecnica ? <span className="stats-positions-ranking-badge stats-positions-ranking-badge-tecnica">T</span> : null}
                                                {isLesionat ? ' 🏥' : ''}
                                            </span>
                                            {mote ? <span className="stats-positions-ranking-fullname">{fullName}</span> : null}
                                        </div>
                                        <MiniChartsWithConnectors pestanyes={item.pestanyes} cordos={item.cordos} unpaintedProvesCount={item.unpaintedProvesCount} />
                                    </li>
                                );
                            })}
                        </ol>
                        {rankingLimitWorst < filteredScores.length ? (
                            <button 
                                className="stats-positions-ranking-load-more"
                                onClick={() => setRankingLimitWorst(prev => prev + 10)}
                            >
                                Carregar 10 més
                            </button>
                        ) : null}
                    </div>
                </div>
            )}
        </div>
    );
}

export default StatsComparativaPosicions;
